import { useEffect, useRef } from "react";

import { removeCellFromSnapshot, writeCodeCellSnapshot } from "@moment/state";

import { isFulfilledResult, isRejectedResult } from "~/components/canvas/easel/runtime/interfaces";
import type { CachedCell, CellInfo } from "~/models/cell";

/**
 * Returns true if the value is a DOM node whose serialised form (outerHTML)
 * may not be ready until after React finishes rendering children into it.
 */
function isDomNode(value: unknown): value is Node {
	try {
		return typeof Node !== "undefined" && value instanceof Node;
	} catch {
		return false;
	}
}

/** How long to wait (ms) for DOM children before giving up and writing anyway. */
const DOM_OBSERVE_TIMEOUT_MS = 500;

/**
 * Wait for a DOM node to have meaningful content, then call `callback`.
 * Uses a MutationObserver so we capture the full rendered tree rather than
 * relying on frame timing.  Falls back to the timeout if nothing happens.
 *
 * Returns a cleanup function that cancels both the observer and timer.
 */
function observeDomThenWrite(node: Node, callback: () => void): () => void {
	// If the node already has children, write immediately.
	if (node.childNodes.length > 0) {
		callback();
		return () => {};
	}

	let done = false;
	const finish = () => {
		if (done) {
			return;
		}
		done = true;
		observer.disconnect();
		clearTimeout(timer);
		callback();
	};

	const observer = new MutationObserver(finish);
	observer.observe(node, { childList: true, subtree: true });

	const timer = setTimeout(finish, DOM_OBSERVE_TIMEOUT_MS);

	return () => {
		if (!done) {
			done = true;
			observer.disconnect();
			clearTimeout(timer);
		}
	};
}

/**
 * Writes a diagnostics snapshot for a code cell to the document's
 * `.moment/diagnostics/{pageId}/snapshot.log` file so that agents can see
 * the current output of every cell.
 *
 * Unlike the error log, this writes on ALL result states (success, error,
 * static error, compile error) and always overwrites the snapshot.
 */
export function useLogCellDiagnostics(
	cellInfo: CellInfo | undefined,
	cachedCell: CachedCell | undefined,
	documentId: string | undefined,
	pageId: string | undefined
) {
	const lastWrittenKey = useRef<string | null>(null);

	useEffect(() => {
		if (!documentId || !cellInfo) {
			return;
		}

		const cellId = cellInfo.cell.id;
		const cellName = cellInfo.cell.cellName || undefined;

		// Compile error
		if (cellInfo.status === "error") {
			const key = `compile:${cellId}:${cellInfo.message}`;
			if (lastWrittenKey.current === key) {
				return;
			}
			writeCodeCellSnapshot({
				documentId,
				pageId,
				cellId,
				cellName,
				result: {
					type: "ERROR",
					staticError: new Error(cellInfo.message),
				},
			});
			// Set dedup key only after write — writeCodeCellSnapshot no-ops when
			// pageId is undefined, so caching the key early would prevent the
			// write once pageId becomes available.
			if (pageId) {
				lastWrittenKey.current = key;
			}
			return;
		}

		const result = cachedCell?.result;
		if (!result) {
			return;
		}

		// Static error (parse/compile error while previous result may still be running)
		if (result.staticError) {
			const key = `static:${cellId}:${result.staticError.message}`;
			if (lastWrittenKey.current === key) {
				return;
			}
			writeCodeCellSnapshot({
				documentId,
				pageId,
				cellId,
				cellName,
				result: {
					type: result.type,
					staticError: result.staticError,
				},
			});
			if (pageId) {
				lastWrittenKey.current = key;
			}
			return;
		}

		// Runtime error
		if (isRejectedResult(result)) {
			const key = `runtime:${cellId}:${result.runtimeError.message}`;
			if (lastWrittenKey.current === key) {
				return;
			}
			writeCodeCellSnapshot({
				documentId,
				pageId,
				cellId,
				cellName,
				result: {
					type: "REJECTED",
					runtimeError: result.runtimeError,
				},
			});
			if (pageId) {
				lastWrittenKey.current = key;
			}
			return;
		}

		// Fulfilled (success)
		if (isFulfilledResult(result)) {
			const writeSnapshot = () => {
				let valuePreview: string;
				if (isDomNode(result.value)) {
					// DOM nodes always produce a generic toString like "[object HTMLDivElement]",
					// so use outerHTML (which reflects actual rendered content) for dedup.
					valuePreview = (result.value as Element).outerHTML?.slice(0, 500) ?? "dom";
				} else {
					try {
						valuePreview = JSON.stringify(result.value)?.slice(0, 200) ?? "undefined";
					} catch {
						valuePreview = String(result.value).slice(0, 200);
					}
				}
				const key = `fulfilled:${cellId}:${valuePreview}`;
				if (lastWrittenKey.current === key) {
					return;
				}
				writeCodeCellSnapshot({
					documentId,
					pageId,
					cellId,
					cellName,
					result: {
						type: "FULFILLED",
						value: result.value,
					},
				});
				if (pageId) {
					lastWrittenKey.current = key;
				}
			};

			// DOM nodes need a deferred write so React has time to render
			// children into the container before we read outerHTML.
			if (isDomNode(result.value)) {
				const cleanup = observeDomThenWrite(result.value, writeSnapshot);
				return cleanup;
			}

			writeSnapshot();
			return;
		}

		// Pending — skip writing unless there's a static error (handled above)
	}, [cellInfo, cachedCell?.result, documentId, pageId]);

	// When this cell component unmounts (i.e. the cell was deleted from the
	// document), remove its stale entry from the snapshot so agents don't see
	// output for cells that no longer exist.
	const cellId = cellInfo?.cell.id;
	useEffect(() => {
		if (!documentId || !pageId || !cellId) {
			return;
		}
		return () => {
			removeCellFromSnapshot({ documentId, pageId, cellId });
		};
	}, [documentId, pageId, cellId]);
}
